import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { PButtonPure, PIcon, PSpinner, PText } from "@porsche-design-system/components-react"
import type { PIconProps } from "@porsche-design-system/components-react"
import {
	colorCanvas,
	colorContrastHigh,
	colorPrimary,
	durationSm,
	easeInOut,
	radius4Xl,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import type { ActionItemWithLinks } from "@slfinrtl/ui-bff-routes"
import { useState } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Tooltip } from "src/components"

interface OpenGalleryLinkProps {
	actionItem: ActionItemWithLinks
	icon: PIconProps["name"]
	onClick?: () => Promise<void>
}

export const OpenGalleryLink = (props: OpenGalleryLinkProps) => {
	const { actionItem, icon, onClick } = props

	const [isLoading, setIsLoading] = useState(false)

	const onOpenGalleryClick = async () => {
		if (!onClick) {
			return
		}
		setIsLoading(true)
		await onClick()
		setIsLoading(false)
	}

	const isDisabled = actionItem.disabledHint !== undefined

	return (
		<Tooltip content={actionItem.disabledHint}>
			<StyledButtonPure
				disabled={isDisabled}
				onClick={isLoading ? undefined : onOpenGalleryClick}
				icon="none"
				size="xs"
			>
				<Content $isDisabled={isDisabled}>
					{actionItem.label && (
						<PText size="inherit" color="inherit">
							{actionItem.label}
						</PText>
					)}
					{isLoading ? (
						<PSpinner size="inherit" color="inherit" />
					) : (
						<PIcon name={icon} size="inherit" color="inherit" />
					)}
				</Content>
			</StyledButtonPure>
		</Tooltip>
	)
}

const StyledButtonPure = styled(PButtonPure)`
	padding: 4px 9px;
	background: ${colorPrimary};
	border-radius: ${radius4Xl};
	overflow: hidden;

	${({ disabled }) =>
		!disabled &&
		css`
		@media (hover: hover) and (pointer: fine) {
			transition: background-color ${durationSm} ${easeInOut};
			
			&:hover {
				background: ${colorContrastHigh};
			}
		}`}
`

const Content = styled("span", { shouldForwardProp })<{ $isDisabled: boolean }>`
	color: ${({ $isDisabled }) => ($isDisabled ? "#aaa" : colorCanvas)};
	display: flex;
	align-items: center;
	gap: ${spacingStaticSm};
`
